// components/ThemeChildComponents.tsx
'use client';

import '@aws-amplify/ui-react/styles.css';
// Must come after Amplify's stylesheet so its overrides take precedence.
import '@/components/theme/modern.css';
import { ThemeProvider, View } from '@aws-amplify/ui-react';
import { theme } from '@/components/theme/theme';
import { createContext, useContext, useState, useEffect } from 'react';

const ColorModeContext = createContext<'light' | 'dark'>('light');

/** Resolved color mode ('system' already collapsed to light/dark). */
export const useAppColorMode = () => useContext(ColorModeContext);

export default function ThemeChildComponents({ children }: { children: React.ReactNode }) {
  // 1) Match SSR on first paint; update after mount.
  const [mode, setMode] = useState<'light' | 'dark' | 'system'>('system');
  const [resolvedMode, setResolvedMode] = useState<'light' | 'dark'>('light');

  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    // prefer saved choice; else system preference
    const saved = (window.localStorage.getItem('colorMode') as 'light' | 'dark' | 'system' | null);

    if (saved && saved !== 'system') {
      setMode(saved);
      setResolvedMode(saved);
      return;
    }

    setMode(mq.matches ? 'dark' : 'light');
    setResolvedMode(mq.matches ? 'dark' : 'light');

    // optional: stay in sync with OS changes
    const onChange = (e: MediaQueryListEvent) => {
      setMode(e.matches ? 'dark' : 'light');
      setResolvedMode(e.matches ? 'dark' : 'light');
    };
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);


  return (
    <ColorModeContext.Provider value={resolvedMode}>
    <ThemeProvider theme={theme} colorMode={mode}>
      <main>
        <View 
        backgroundColor={"brand.background"}
        // minHeight keeps the themed background filling short pages; overflowX 'clip' (unlike 'hidden') keeps the sticky header working.
        // Deliberately not position: sticky on mobile: the site header (fixed on mobile) sits inside this wrapper, and a sticky ancestor makes it jump while scrolling on iOS Safari.
        style={{ height: '100%', minHeight: '100vh', width: '100%', display: 'flex', flexDirection: 'column', alignContent: 'center', overflowX: 'clip'}}
        >
            {children}
        </View>
      </main>
    </ThemeProvider>
    </ColorModeContext.Provider>
  )
}